/**
 * ClassificationManagerDialog - Modal para gerenciar classificações de templates
 * 
 * Permite listar, criar, editar e excluir classificações de templates WhatsApp.
 * Segue o padrão de design do LabelManager.tsx
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, MoreVertical, Pencil, Trash2, Tag, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { showToast } from "@/lib/toast";
import { logger } from "@/lib/logger";
import { CreateClassificationDialog } from "./CreateClassificationDialog";
import { EditClassificationDialog } from "./EditClassificationDialog";

// Interface para classificação de template
interface TemplateClassification {
  id: string;
  name: string;
  color: string;
  description: string | null;
  created_at: string;
}

// Props do componente
interface ClassificationManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string | undefined;
  onUpdate: () => void;
}

export function ClassificationManagerDialog({
  open,
  onOpenChange,
  tenantId,
  onUpdate,
}: ClassificationManagerDialogProps) {
  // Estados do componente
  const [classifications, setClassifications] = useState<TemplateClassification[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState<TemplateClassification | null>(null);
  const [templateCount, setTemplateCount] = useState<number>(0);
  const [deleting, setDeleting] = useState(false);

  // Busca classificações quando o modal abre
  useEffect(() => {
    if (open && tenantId) {
      fetchClassifications();
    }
  }, [open, tenantId]);

  /**
   * Busca todas as classificações do tenant
   */
  const fetchClassifications = async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("template_classifications")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("name", { ascending: true });

      if (error) {
        logger.error("[ClassificationManager] Erro ao buscar classificações:", error.message);
        showToast.error("Erro ao carregar classificações");
        return;
      }

      setClassifications(data || []);
    } catch (err) {
      logger.error("[ClassificationManager] Exceção ao buscar classificações:", err);
      showToast.error("Erro ao carregar classificações");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verifica quantos templates usam a classificação antes de excluir
   */
  const checkTemplateUsage = async (classificationId: string): Promise<number> => {
    if (!tenantId) return 0;

    const { count, error } = await supabase
      .from("whatsapp_templates")
      .select("*", { count: "exact", head: true })
      .eq("classification_id", classificationId)
      .eq("tenant_id", tenantId);

    if (error) {
      logger.error("[ClassificationManager] Erro ao verificar uso:", error.message);
      return 0;
    }

    return count || 0;
  };

  /**
   * Abre o dialog de confirmação de exclusão
   */
  const handleDeleteClick = async (classification: TemplateClassification) => {
    setSelectedClassification(classification);
    const count = await checkTemplateUsage(classification.id);
    setTemplateCount(count);
    setDeleteDialogOpen(true);
  };

  /**
   * Confirma e executa a exclusão da classificação
   */
  const handleConfirmDelete = async () => {
    if (!selectedClassification) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from("template_classifications")
        .delete()
        .eq("id", selectedClassification.id);

      if (error) {
        logger.error("[ClassificationManager] Erro ao excluir:", error.message);
        showToast.error("Erro ao excluir classificação");
        return;
      }

      showToast.success("Classificação excluída com sucesso");
      fetchClassifications();
      onUpdate();
    } catch (err) {
      logger.error("[ClassificationManager] Exceção ao excluir:", err);
      showToast.error("Erro ao excluir classificação");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedClassification(null);
    }
  };

  /**
   * Abre o dialog de edição
   */
  const handleEditClick = (classification: TemplateClassification) => {
    setSelectedClassification(classification);
    setEditDialogOpen(true);
  };

  /**
   * Callback após criar/editar classificação
   */
  const handleSuccess = () => {
    fetchClassifications();
    onUpdate();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Gerenciar Classificações
            </DialogTitle>
            <DialogDescription>
              Organize seus templates por classificação para facilitar a busca e filtragem.
            </DialogDescription>
          </DialogHeader>

          {/* Header com contagem e botão de criar */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-muted-foreground">
              {classifications.length} classificação(ões)
            </span>
            <Button
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
              disabled={!tenantId}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Classificação
            </Button>
          </div>

          {/* Lista de classificações */}
          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : classifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Tag className="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Nenhuma classificação criada
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Clique em "Nova Classificação" para começar
                </p>
              </div>
            ) : (
              classifications.map((classification) => (
                <div
                  key={classification.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Indicador de cor */}
                    <div
                      className="w-4 h-4 rounded-full mt-0.5 flex-shrink-0"
                      style={{ backgroundColor: classification.color }}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {classification.name}
                      </span>
                      {classification.description && (
                        <span className="text-xs text-muted-foreground mt-0.5">
                          {classification.description}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Menu de ações */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditClick(classification)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteClick(classification)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de criação */}
      <CreateClassificationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        tenantId={tenantId}
        onSuccess={handleSuccess}
      />

      {/* Dialog de edição */}
      <EditClassificationDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        classification={selectedClassification}
        onSuccess={handleSuccess}
      />

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir classificação?</AlertDialogTitle>
            <AlertDialogDescription>
              {templateCount > 0 ? (
                <>
                  <strong>{templateCount} template(s)</strong> usam esta classificação
                  e ficarão sem classificação após a exclusão.
                </>
              ) : (
                "Esta ação não pode ser desfeita."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
